import { useCallback, useEffect, useState } from "react";
import { CheckCircle2, Loader2, MessageCircle, Search, Send } from "lucide-react";
import { toast } from "sonner";
import { Input } from "../ui/input";
import { api, formatApiError } from "../../lib/api";
import { EmptyRow, Table, fmtDateTime } from "../office/ui";

const DistributorPicker = ({ value, onChange }) => {
  const [q, setQ] = useState("");
  const [matches, setMatches] = useState([]);
  useEffect(() => {
    if (value || !q.trim()) return undefined;
    const t = setTimeout(() => api.get("/admin/distributors", { params: { q } }).then(({ data }) => setMatches(data.slice(0, 8))), 300);
    return () => clearTimeout(t);
  }, [q, value]);
  if (value)
    return (
      <div className="flex items-center justify-between rounded-xl border border-leaf/50 bg-leaf/10 px-4 py-3" data-testid="send-dist-selected">
        <div className="flex items-center gap-3"><CheckCircle2 className="h-5 w-5 text-leaf-deep" /><div><p className="text-sm font-semibold">{value.name}</p><p className="font-mono text-xs text-ink/60">ID {value.dist_id} · {value.phone} · {value.country}</p></div></div>
        <button type="button" onClick={() => { onChange(null); setQ(""); setMatches([]); }} className="text-xs underline-offset-2 hover:underline" data-testid="send-dist-change">Cambiar</button>
      </div>
    );
  return (
    <div>
      <div className="relative">
        <Search className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-ink/50" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar distribuidor por ID, nombre o celular" className="h-11 bg-white pl-10" data-testid="send-dist-search" />
      </div>
      {matches.length > 0 && (
        <ul className="mt-2 overflow-hidden rounded-xl border border-ink/10 bg-white" data-testid="send-dist-matches">
          {matches.map((m) => (
            <li key={m.dist_id}><button type="button" onClick={() => onChange(m)} className="flex w-full items-center justify-between px-4 py-2.5 text-left text-sm hover:bg-paper" data-testid={`send-dist-option-${m.dist_id}`}><span>{m.name} <span className="text-ink/60">· {m.phone}</span></span><span className="font-mono text-xs text-leaf-deep">ID {m.dist_id}</span></button></li>
          ))}
        </ul>
      )}
    </div>
  );
};

export const CodesSend = ({ onSent }) => {
  const [summary, setSummary] = useState([]);
  const [dist, setDist] = useState(null);
  const [phone, setPhone] = useState("");
  const [qty, setQty] = useState({});
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const loadSummary = useCallback(() => api.get("/admin/codes/summary").then(({ data }) => setSummary(data)), []);
  useEffect(() => {
    loadSummary();
  }, [loadSummary]);
  useEffect(() => {
    if (dist) setPhone(dist.phone);
  }, [dist]);

  const items = Object.entries(qty).filter(([, n]) => Number(n) > 0).map(([platform, n]) => ({ platform, count: Number(n) }));
  const total = items.reduce((a, i) => a + i.count, 0);

  const send = async (e) => {
    e.preventDefault();
    if (!dist) return toast.error("Selecciona el distribuidor que recibirá los códigos");
    if (!items.length) return toast.error("Indica cuántos códigos enviar de al menos una plataforma");
    setBusy(true);
    try {
      const { data } = await api.post("/admin/codes/send", { dist_id: dist.dist_id, phone, items, note });
      setResult(data);
      setQty({});
      window.open(data.whatsapp_url, "_blank", "noopener");
      toast.success(`${data.total} códigos asignados a ${data.distributor_name}. Se abrió WhatsApp.`);
      loadSummary();
      onSent?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={send} className="grid gap-6 lg:grid-cols-[1.1fr_1fr]" data-testid="codes-send">
      <div className="space-y-5">
        <div>
          <p className="mb-2 text-sm font-semibold">1. ¿A quién se envían?</p>
          <DistributorPicker value={dist} onChange={setDist} />
        </div>
        <div>
          <label className="mb-2 block text-sm font-semibold">2. WhatsApp de destino</label>
          <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Número con lada, ej. 523312345678" className="h-11 bg-white font-mono" data-testid="send-phone" />
          <p className="mt-1 text-xs text-ink/60">Por defecto el celular del distribuidor. Si tiene 10 dígitos se asume México (+52).</p>
        </div>
        <div>
          <p className="mb-2 text-sm font-semibold">3. ¿Cuántos códigos de cada plataforma?</p>
          <div className="space-y-2">
            {summary.map((p) => (
              <div key={p.platform} className="flex items-center justify-between gap-3 rounded-xl border border-ink/10 bg-white px-4 py-2.5" data-testid={`send-row-${p.platform.replace(" ", "-")}`}>
                <div><p className="text-sm font-semibold">{p.platform}</p><p className="font-mono text-[11px] text-ink/60">{p.disponibles} disponibles</p></div>
                <div className="flex items-center gap-1">
                  {[1, 5, 10].map((n) => <button key={n} type="button" onClick={() => setQty({ ...qty, [p.platform]: n })} className="rounded-full border border-ink/15 px-2.5 py-1 text-[11px] font-semibold hover:bg-paper">{n}</button>)}
                  <Input type="number" min={0} max={Math.min(100, p.disponibles)} value={qty[p.platform] ?? ""} onChange={(e) => setQty({ ...qty, [p.platform]: e.target.value })} placeholder="0" className="h-9 w-20 bg-white text-center" data-testid={`send-qty-${p.platform.replace(" ", "-")}`} />
                </div>
              </div>
            ))}
          </div>
        </div>
        <div>
          <label className="mb-2 block text-sm font-semibold">4. Mensaje adicional (opcional)</label>
          <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Ej. Actívalos antes del viernes" className="h-11 bg-white" data-testid="send-note" />
        </div>
        <button type="submit" disabled={busy || !dist || !total} className="btn-leaf disabled:opacity-50" data-testid="send-submit">
          {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />} Asignar {total || ""} código{total === 1 ? "" : "s"} y abrir WhatsApp
        </button>
      </div>
      <div className="rounded-[1.5rem] border border-ink/10 bg-white p-5" data-testid="send-result">
        {result ? (
          <>
            <p className="font-display text-lg font-semibold">Último envío registrado</p>
            <p className="mt-1 text-sm text-ink/70">{result.distributor_name} (ID {result.dist_id}) · {result.phone} · {fmtDateTime(result.created_at)}</p>
            <pre className="mt-4 whitespace-pre-wrap rounded-xl bg-paper p-4 font-sans text-sm leading-relaxed" data-testid="send-message">{result.message}</pre>
            <a href={result.whatsapp_url} target="_blank" rel="noreferrer" className="btn-ink mt-4 !px-5 !py-3 !text-sm" data-testid="send-reopen-wa"><MessageCircle className="h-4 w-4" /> Volver a abrir WhatsApp</a>
          </>
        ) : (
          <>
            <p className="font-display text-lg font-semibold">¿Cómo funciona?</p>
            <ol className="mt-3 list-decimal space-y-2 pl-5 text-sm text-ink/75">
              <li>Elige al distribuidor; su celular se usa como WhatsApp de destino (puedes cambiarlo).</li>
              <li>Indica cuántos códigos de cada plataforma quieres mandar; puedes combinar varias en un mismo mensaje.</li>
              <li>Al confirmar, los códigos se marcan como <strong>enviados</strong> a ese ID, se guarda el registro y se abre WhatsApp con el mensaje listo.</li>
            </ol>
          </>
        )}
      </div>
    </form>
  );
};

export const CodesHistory = () => {
  const [q, setQ] = useState("");
  const [items, setItems] = useState(null);
  useEffect(() => {
    const t = setTimeout(() => api.get("/admin/codes/shipments", { params: { q } }).then(({ data }) => setItems(data)), 250);
    return () => clearTimeout(t);
  }, [q]);
  return (
    <div data-testid="codes-history">
      <div className="relative max-w-md">
        <Search className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-ink/50" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por ID, nombre, teléfono o código" className="h-11 bg-white pl-10" data-testid="history-search" />
      </div>
      <Table headers={["Fecha", "Distribuidor (ID)", "WhatsApp", "Códigos", "Total", ""]} testId="history-table" minWidth="min-w-[900px]">
        {!items && <tr><td colSpan={6} className="px-4 py-8 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin" /></td></tr>}
        {items && !items.length && <EmptyRow cols={6} text="Aún no se han enviado códigos." testId="history-empty" />}
        {items?.map((s, i) => (
          <tr key={s.id} className="border-b border-ink/5 align-top last:border-0" data-testid={`history-row-${i}`}>
            <td className="whitespace-nowrap px-4 py-3 text-ink/70">{fmtDateTime(s.created_at)}</td>
            <td className="px-4 py-3"><p className="font-semibold">{s.distributor_name}</p><p className="font-mono text-xs text-ink/60">ID {s.dist_id}</p></td>
            <td className="px-4 py-3 font-mono text-xs">{s.phone}</td>
            <td className="px-4 py-3 text-xs">{Object.entries(s.groups).map(([p, codes]) => <p key={p}><span className="font-semibold">{p}:</span> <span className="font-mono">{codes.join(", ")}</span></p>)}</td>
            <td className="px-4 py-3 font-display text-base font-semibold">{s.total}</td>
            <td className="px-4 py-3"><a href={s.whatsapp_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 rounded-full border border-ink/15 px-3 py-1.5 text-xs font-semibold hover:bg-paper" data-testid={`history-resend-${i}`}><MessageCircle className="h-3.5 w-3.5" /> Reenviar</a></td>
          </tr>
        ))}
      </Table>
    </div>
  );
};
